import { ContainerMediaData } from "../components/organism/ViewPhotoCollectionDialog";
import {
  isCustomPhotoType,
  getPhotoTypeTitleForDownload,
} from "./photoTypeTitle";
import {
  CustomPhotoTypeItem,
  PhotoTypeMediaInfo,
  SimpleContainerMediaDto,
} from "../store/apis/tasks/taskDetail/interface";
import { toPascalCase } from "./transform";

/**
 * @description 대문자 영어문자열 첫글자 대문자 및 소문자 변환
 */
export const convertToManagerType = (type: string) => {
  const convertedType = type.replace(/_/g, " ").toLowerCase();
  return convertedType.charAt(0).toUpperCase() + convertedType.slice(1);
};

/**
 * @description 이메일 앞 @ *** 마스킹
 * @param email
 * @param removeLength : 제거될 문자열 개수 기본값 3
 */
export const changeEmailCharacters = (
  email: string,
  removeLength: number = 3
) => {
  const atIndex = email.indexOf("@");
  const emailPrefix = email.substring(0, atIndex);
  const modifiedEmail =
    emailPrefix.substring(0, emailPrefix.length - removeLength) +
    "***" +
    email.substring(atIndex);

  return modifiedEmail;
};

/**
 * @description 퍼센트 구하기
 */
export const calculatePercentage = (part: number, total: number) => {
  return Math.floor((part / total) * 100);
};

/**
 * @description 메인 키테고리 이름형식 Metal, Glass ...
 */
export const formatCategoryName = (item: string[]) => {
  const categoryString = item
    .map((item) => item[0] + item.slice(1).toLowerCase().replace(/_/g, "-"))
    .join(", ");

  return categoryString;
};

export const formatSingleContainerMedia = (
  containerData: any
): { folderName: string; images: ContainerMediaData[] } => {
  const {
    containerNo,
    sampleBaleSimpleContainerMedias,
    emptySimpleContainerMedias,
    quarterLoadedSimpleContainerMedias,
    halfLoadedSimpleContainerMedias,
    fullLoadedSimpleContainerMedias,
    oneDoorClosedSimpleContainerMedias,
    bothDoorsClosedSimpleContainerMedias,
    closeUpSealSimpleContainerMedias,
    extraSimpleContainerMedias,
    photoTypeMedias,
    photoTypeRequests,
  } = containerData;

  const allMediaDataByType: Record<string, SimpleContainerMediaDto[]> = {
    SAMPLE_BALE: sampleBaleSimpleContainerMedias || [],
    EMPTY: emptySimpleContainerMedias || [],
    QUARTER_LOADED: quarterLoadedSimpleContainerMedias || [],
    HALF_LOADED: halfLoadedSimpleContainerMedias || [],
    FULL_LOADED: fullLoadedSimpleContainerMedias || [],
    ONE_DOOR_CLOSED: oneDoorClosedSimpleContainerMedias || [],
    BOTH_DOORS_CLOSED: bothDoorsClosedSimpleContainerMedias || [],
    CLOSE_UP_SEAL: closeUpSealSimpleContainerMedias || [],
    EXTRA: extraSimpleContainerMedias || [],
  };

  if (photoTypeMedias) {
    Object.entries(photoTypeMedias).forEach(
      ([typeCode, info]: [string, PhotoTypeMediaInfo | CustomPhotoTypeItem[]]) => {
        if (typeCode === "CUSTOM" && Array.isArray(info)) {
          (info as CustomPhotoTypeItem[]).forEach((customItem, index) => {
            if (customItem.medias && customItem.medias.length > 0) {
              const customKey = `CUSTOM_${customItem.id ?? `item_${index}`}`;
              allMediaDataByType[customKey] = customItem.medias;
            }
          });
        } else if (isCustomPhotoType(typeCode) && !Array.isArray(info) && info?.medias) {
          allMediaDataByType[typeCode] = info.medias;
        }
      }
    );
  }

  const images: ContainerMediaData[] = [];

  Object.keys(allMediaDataByType).forEach((typeCode) => {
    const mediaList = allMediaDataByType[typeCode];

    if (mediaList.length > 0) {
      const title = getPhotoTypeTitleForDownload(typeCode, photoTypeRequests, photoTypeMedias);
      mediaList.forEach((media, index) => {
        images.push({
          createAt: media.containerMediaCreatedAt,
          mediaUrl: media.containerPhotoSimpleMedia?.mediaUrl,
          originalFileName: `${containerNo || "-"}_${toPascalCase(
            title
          )}${String(index + 1).padStart(2, "0")}.jpg`,
          latitude: media.coordinate?.latitude
            ? media.coordinate?.latitude
            : undefined,
          longitude: media.coordinate?.longitude
            ? media.coordinate?.longitude
            : undefined,
          zoomLevel: media.coordinate?.zoomLevel,
          mediaCoordinateRange: media.coordinate?.mediaCoordinateRange,
        });
      });
    }
  });

  return {
    folderName: containerNo || "-",
    images,
  };
};

// 만료일 포맷 함수 추가
export const formatCreditCardExpiryDate = (expiryDate: string) => {
  if (!expiryDate || expiryDate === "-") return "-";

  if (expiryDate.length === 4) {
    const year = `20${expiryDate.substring(0, 2)}`;
    const month = expiryDate.substring(2, 4);
    return `${year}-${month}`;
  }

  return expiryDate;
};

// 신용카드 번호 마스킹
export const formatCreditCardMasking = (cardNumber: string) => {
  if (!cardNumber || cardNumber.trim() === "") {
    return "-";
  }

  const part1 = cardNumber.substring(0, 4);
  const part2 = cardNumber.substring(4, 6);
  const part3 = cardNumber.substring(12, 14);

  const formatted = `${part1}-${part2}**-****-${part3}**`;
  return formatted;
};

export const formatNumberWithDecimal = (value: number) => {
  // 소수점 이하가 0인지 확인하는 더 확실한 방법
  if (Number.isInteger(value) || value.toFixed(2).endsWith(".00")) {
    // 정수로 변환하여 표시
    return Math.round(value).toLocaleString("ko-KR");
  } else {
    // 소수점이 있는 경우 소수점 두 자리까지 표시
    return value.toLocaleString("ko-KR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
};
